import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly iconOnly?: boolean;
    readonly interactive?: boolean;
    readonly onClick?: (e: MouseEvent) => void;
    readonly size?: 'm' | 'sm';
    readonly variant?: 'active-card' | 'error' | 'gray' | 'info' | 'orange' | 'success' | 'warning';
};

const baseClasses =
    'inline-flex flex-row items-center justify-center font-sans font-medium tabular-nums transition-colors duration-200 ease select-none whitespace-nowrap border';

const activeAncestorClasses =
    'in-data-[is-active=true]:!bg-status-active-surface in-data-[is-active=true]:!border-status-active-border in-data-[is-active=true]:!text-status-active-text in-data-[is-active]:transition-none';

const iconSizeClasses = '[&>svg]:h-3 [&>svg]:w-3 [&>svg]:shrink-0';
const iconOnlyClasses = 'h-[24px] w-[24px] gap-0 text-meta';
const contentClasses: Record<NonNullable<BadgeProps['size']>, string> = {
    m: 'h-[24px] min-w-[32px] px-m gap-xs text-meta [&:has(>svg:first-child)]:pl-s [&:has(>svg:last-child)]:pr-s',
    sm: 'h-[18px] min-w-[20px] px-s gap-xs text-meta [&:has(>svg:first-child)]:pl-xs [&:has(>svg:last-child)]:pr-xs',
};

const variantBaseClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
    'active-card': 'bg-status-active-surface border-status-active-border text-status-active-text',
    error: 'bg-status-error-surface border-status-error-border text-status-error-text',
    gray: 'bg-status-neutral-surface border-status-neutral-border text-status-neutral-text',
    info: 'bg-status-info-surface border-status-info-border text-status-info-text',
    orange: 'bg-status-orange-surface border-status-orange-border text-status-orange-text',
    success: 'bg-status-success-surface border-status-success-border text-status-success-text',
    warning: 'bg-status-warning-surface border-status-warning-border text-status-warning-text',
};

const variantHoverClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
    'active-card':
        'hover:bg-status-active-hover-surface hover:border-status-active-hover-border hover:text-status-active-hover-text hover:scale-[1.01]',
    error: 'hover:bg-status-error-hover-surface hover:border-status-error-hover-border hover:text-status-error-hover-text hover:scale-[1.01]',
    gray: 'hover:bg-status-neutral-hover-surface',
    info: 'hover:bg-status-info-hover-surface',
    orange: 'hover:bg-status-orange-hover-surface hover:border-status-orange-hover-border hover:text-status-orange-hover-text hover:scale-[1.01]',
    success: 'hover:bg-status-success-hover-surface',
    warning: 'hover:bg-status-warning-hover-surface',
};

const variantActiveClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
    'active-card':
        'active:bg-status-active-pressed-surface active:border-status-active-pressed-border active:text-status-active-pressed-text active:scale-[0.98]',
    error: 'active:bg-status-error-pressed-surface active:border-status-error-pressed-border active:text-status-error-pressed-text active:scale-[0.98]',
    gray: 'active:bg-status-neutral-pressed-surface active:border-status-neutral-pressed-border active:text-status-neutral-pressed-text active:scale-[0.98]',
    info: 'active:bg-status-info-pressed-surface active:border-status-info-pressed-border active:text-status-info-pressed-text active:scale-[0.98]',
    orange: 'active:bg-status-orange-pressed-surface active:border-status-orange-pressed-border active:text-status-orange-pressed-text active:scale-[0.98]',
    success:
        'active:bg-status-success-pressed-surface active:border-status-success-pressed-border active:text-status-success-pressed-text active:scale-[0.98]',
    warning:
        'active:bg-status-warning-pressed-surface active:border-status-warning-pressed-border active:text-status-warning-pressed-text active:scale-[0.98]',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
    (
        {
            children,
            className = '',
            disabled = false,
            iconOnly = false,
            interactive = false,
            onClick,
            size = 'm',
            variant = 'gray',
            ...props
        },
        ref,
    ) => {
        const isClickable = interactive || !is.undefined(onClick);

        return (
            // biome-ignore lint/a11y/noStaticElementInteractions: suppressed due to migration
            // biome-ignore lint/a11y/useKeyWithClickEvents: suppressed due to migration
            <span
                ref={ref}
                className={clsx(
                    baseClasses,
                    activeAncestorClasses,
                    iconOnly ? 'rounded-[50%]' : 'rounded-full',
                    iconOnly ? iconOnlyClasses : contentClasses[size],
                    iconSizeClasses,
                    variantBaseClasses[variant],
                    disabled && 'opacity-50',
                    isClickable && !disabled && variantHoverClasses[variant],
                    isClickable && !disabled && variantActiveClasses[variant],
                    isClickable && !disabled ? 'cursor-pointer' : 'cursor-default',
                    className,
                )}
                data-variant={variant}
                onClick={disabled ? undefined : onClick}
                {...props}
            >
                {children}
            </span>
        );
    },
);

Badge.displayName = 'Badge';

/**
 * Unstyled button around a `Badge`, for a badge that opens something (e.g. as a Radix `asChild`
 * menu trigger). The trigger's button semantics and ARIA land on the button; the badge inside keeps
 * its look. Pass `disabled` to both so a disabled badge also looks inert.
 */
export const BadgeButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
    ({ children, className, ...props }, ref) => (
        <button
            ref={ref}
            className={clsx(
                'inline-flex rounded-full border-0 bg-transparent p-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring',
                className,
            )}
            type="button"
            {...props}
        >
            {children}
        </button>
    ),
);

BadgeButton.displayName = 'BadgeButton';
